import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout, checkOrRenewToken, fetchWithAuth, authStatus, getStoredGender, setStoredGender } from './AuthService';
import { updateUserLanguage } from './Api';
import i18n from '../i18n';

// Un utilisateur a accès aux fonctionnalités payantes (écran Note, écran Besoin)
// si son rôle est admin, ou si sa date de fin d'abonnement (expireDate) est dans le futur.
// Les beta-testeurs (role 'tester') passent par ce même calcul : leur expireDate
// est positionnée à +90 jours automatiquement lors de l'attribution du rôle.
const computeHasActiveSubscription = (role, expireDate) => {
  if (role === 'admin') return true;
  if (!expireDate) return false;
  return new Date(expireDate).getTime() > Date.now();
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(undefined);
  const [authLoading, setAuthLoading] = useState(true);
  const [resetToken, setResetToken] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [userKey, setUserKey] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userExpireDate, setUserExpireDate] = useState(null);
  const [userIsCluf, setUserIsCluf] = useState(false);
  // Genre de l'utilisateur ('male' | 'female' | null) : sert aux libellés genrés
  const [userGender, setUserGenderState] = useState(() => getStoredGender());

  const navigate = useNavigate();
  const location = useLocation();

  // Gérer les tokens pour la réinitialisation de mot de passe
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/auth/resetpassword/')) {
      const tokenFromURL = path.split('/auth/resetpassword/')[1];
      if (tokenFromURL) {
        setResetToken(tokenFromURL);
      }
    }
  }, [location]);

  // Initialisation / refresh token
  useEffect(() => {
    const initAuth = async () => {
      try {
        setAuthLoading(true);
        const result = await checkOrRenewToken();

        if (result.status === authStatus.VALID) {
          setToken(result.token);
          setIsUserAuthenticated(true);
          if (result.userKey) {
            setUserKey(result.userKey);
          }
          if (result.gender !== undefined) {
            setUserGenderState(result.gender || null);
          }
          if (result.role !== undefined) {
            setUserRole(result.role || null);
          }
          if (result.expireDate !== undefined) {
            setUserExpireDate(result.expireDate || null);
          }
          if (result.isCluf !== undefined) {
            setUserIsCluf(result.isCluf);
          }
          if (result.language) {
            i18n.changeLanguage(result.language);
          }

          // checkOrRenewToken ne renvoie le rôle/expireDate à jour que lorsqu'un
          // refresh a eu lieu côté serveur (token expiré). Sur un simple rechargement
          // de page avec un token encore valide, on va chercher ces infos via le profil
          // pour que l'accès aux fonctionnalités payantes (Note/Besoin) reste fiable.
          if (result.role === undefined || result.expireDate === undefined || result.isCluf === undefined || result.language === undefined) {
            try {
              const profileRes = await fetchWithAuth('/api/profile');
              if (profileRes.ok) {
                const profile = await profileRes.json();
                setUserRole(profile.role || null);
                setUserExpireDate(profile.expireDate || null);
                if (profile.isCluf !== undefined) {
                  setUserIsCluf(profile.isCluf);
                }
                if (profile.language) {
                  i18n.changeLanguage(profile.language);
                }
              }
            } catch (e) {
              console.error('Erreur récupération profil (rôle/abonnement/cluf/language):', e);
            }
          }
        } else {
          setToken(null);
          setIsUserAuthenticated(false);
          setUserKey(null);
          setUserRole(null);
          setUserExpireDate(null);
          setUserIsCluf(false);
        }
      } catch (err) {
        console.error("Erreur lors de la vérification du token:", err);
        setToken(null);
        setIsUserAuthenticated(false);
        setUserKey(null);
      } finally {
        setAuthLoading(false);
      }
    };

    initAuth();
  }, [location.pathname]);


  // Met à jour le genre en mémoire ET dans le localStorage (appelé au login
  // et après une modification du profil)
  const updateGender = (gender) => {
    const normalized = gender === 'male' || gender === 'female' ? gender : null;
    setStoredGender(normalized);
    setUserGenderState(normalized);
  };

  const handleLogin = (newToken, newUserKey, role, gender = null, expireDate = null, isCluf = false) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUserKey(newUserKey);
    setUserRole(role);
    setUserExpireDate(expireDate || null);
    setUserIsCluf(isCluf);
    updateGender(gender);
    setIsUserAuthenticated(true);
  };

  const handleLogout = () => {
    logout();
    setToken(null);
    setUserKey(null);
    setUserRole(null);
    setUserExpireDate(null);
    setUserGenderState(null);
    setUserIsCluf(false);
    setIsUserAuthenticated(false);
    navigate('/');
  };

  const handleProfile = () => {
    navigate('/profile');
  };

  const updateLanguage = async (language) => {
    if (!token) return;
    try {
      const updatedUser = await updateUserLanguage(language);
      return updatedUser;
    } catch (err) {
      console.error("Erreur updateLanguage:", err);
      throw err;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        isUserAuthenticated,
        authLoading,
        setIsUserAuthenticated,
        resetToken,
        setResetToken,
        activeModal, 
        setActiveModal,
        userKey,
        setUserKey,
        userRole,
        userExpireDate,
        hasActiveSubscription: computeHasActiveSubscription(userRole, userExpireDate),
        userGender,
        updateGender,
        userIsCluf,
        setUserIsCluf,
        handleLogin,
        handleLogout,
        handleProfile,
        updateLanguage
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);